import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const ApiTestPage = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));

  // Test data
  const testUser = {
    name: 'Frontend Test User',
    email: 'frontend@example.com',
    password: 'password123',
    role: 'buyer',
    company: 'Frontend Test Company'
  };

  const testLoginData = {
    email: 'frontend@example.com',
    password: 'password123'
  };

  useEffect(() => {
    checkBackendHealth();
  }, []);

  const checkBackendHealth = async () => {
    try {
      const health = await api.system.healthCheck();
      setResults(prev => ({
        ...prev,
        health: { success: true, data: health }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        health: { success: false, error: error.message }
      }));
    }
  };

  const testRegister = async () => {
    setLoading(true);
    try {
      const response = await api.auth.register(testUser);
      api.utils.setToken(response.token);
      setAuthToken(response.token);
      setUser(response.data.user);
      setResults(prev => ({
        ...prev,
        register: { success: true, data: response }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        register: { success: false, error: error.message }
      }));
    }
    setLoading(false);
  };

  const testLogin = async () => {
    setLoading(true);
    try {
      const response = await api.auth.login(testLoginData);
      api.utils.setToken(response.token);
      setAuthToken(response.token);
      setUser(response.data.user);
      setResults(prev => ({
        ...prev,
        login: { success: true, data: response }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        login: { success: false, error: error.message }
      }));
    }
    setLoading(false);
  };

  const testGetProjects = async () => {
    setLoading(true);
    try {
      const response = await api.projects.getProjects();
      setResults(prev => ({
        ...prev,
        projects: { success: true, data: response }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        projects: { success: false, error: error.message }
      }));
    }
    setLoading(false);
  };

  const testGetVerificationRequests = async () => {
    setLoading(true);
    try {
      const response = await api.verification.getRequests();
      setResults(prev => ({
        ...prev,
        verification: { success: true, data: response }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        verification: { success: false, error: error.message }
      }));
    }
    setLoading(false);
  };

  const testGetMyTransactions = async () => {
    if (!authToken) {
      alert('Please login first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.transactions.getMyTransactions();
      setResults(prev => ({
        ...prev,
        transactions: { success: true, data: response }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        transactions: { success: false, error: error.message }
      }));
    }
    setLoading(false);
  };

  const logout = () => {
    api.utils.removeToken();
    setAuthToken(null);
    setUser(null);
    setResults({});
  };

  const ResultCard = ({ title, result }) => (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <h3 className="text-lg font-semibold mb-2 flex items-center">
        {title}
        {result && (
          <span className={`ml-2 px-2 py-1 rounded text-sm ${
            result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {result.success ? 'Success' : 'Error'}
          </span>
        )}
      </h3>
      {result && (
        <div className="mt-2">
          {result.success ? (
            <div>
              <p className="text-sm text-gray-600 mb-2">Response:</p>
              <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-red-600">
              <p className="font-medium">Error:</p>
              <p className="text-sm">{result.error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">API Integration Test</h1>
          <p className="text-gray-600">
            Testing the connection between Climify frontend and backend
          </p>
        </div>

        {/* Auth Status */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Authentication Status</h2>
          {user ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 font-medium">✅ Authenticated as {user.name}</p>
                <p className="text-sm text-gray-600">Role: {user.role} | Email: {user.email}</p>
              </div>
              <button
                onClick={logout}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          ) : (
            <p className="text-gray-600">❌ Not authenticated</p>
          )}
        </div>

        {/* Test Buttons */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">API Tests</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <button
              onClick={checkBackendHealth}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Health Check
            </button>
            
            <button
              onClick={testRegister}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              Register User
            </button>
            
            <button
              onClick={testLogin}
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              Login User
            </button>
            
            <button
              onClick={testGetProjects}
              disabled={loading}
              className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              Get Projects
            </button>
            
            <button
              onClick={testGetVerificationRequests}
              disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
            >
              Get Verification Requests
            </button>
            
            <button
              onClick={testGetMyTransactions}
              disabled={loading || !authToken}
              className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50"
            >
              Get My Transactions
            </button>
          </div>
          
          {loading && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-blue-500 bg-blue-100">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Test Results</h2>
          
          <ResultCard title="🏥 Backend Health" result={results.health} />
          <ResultCard title="📝 User Registration" result={results.register} />
          <ResultCard title="🔐 User Login" result={results.login} />
          <ResultCard title="🏭 Projects API" result={results.projects} />
          <ResultCard title="✅ Verification API" result={results.verification} />
          <ResultCard title="💳 Transactions API" result={results.transactions} />
        </div>
      </div>
    </div>
  );
};

export default ApiTestPage; 